/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { endpoints } from '@ibm-watson/shared/modules/ia/util/config';
import { validation } from '@ibm-watson/shared/modules/ia/util/validation';
import { getAccessPrefixedUrl } from '../../../ia/util/access-control';

const intentsEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string
) =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/intents`
    ),
    encodeURIComponent(intentName)
  );

const examplesEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string
): string =>
  intentsEndpoint(skillId, useNewIAUpdatedAccess, assistantId, intentName) +
  '/examples';

const exampleEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string,
  text: string
): string =>
  urlJoin(
    examplesEndpoint(skillId, useNewIAUpdatedAccess, assistantId, intentName),
    encodeURIComponent(text)
  );

const batchRemoveEndpoint = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): string =>
  urlJoin(
    endpoints.store,
    getAccessPrefixedUrl(
      useNewIAUpdatedAccess,
      assistantId,
      `skills/${skillId}/workspace/content`
    )
  );

const validateAll = (skillId, intentName, example, examples) => {
  const validationErrors = [];
  const skillErrors = validation.validateGuid(skillId);
  if (skillErrors) {
    validationErrors.push(skillErrors);
  }
  if (intentName) {
    const intentNameErrors = validation.validateIntent({ intent: intentName });
    if (intentNameErrors) {
      validationErrors.push(intentNameErrors);
    }
  }
  if (example) {
    const exampleErrors = validation.validateExample(example);
    if (exampleErrors) {
      validationErrors.push(exampleErrors);
    }
  }
  if (examples) {
    const examplesErrors = validation.validateExamples(examples);
    if (examplesErrors) {
      validationErrors.push(examplesErrors);
    }
  }

  if (validationErrors.length) {
    return Promise.reject({ data: { error: validationErrors } });
  }
  return null;
};

export const createExample = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string,
  example: any
) => {
  const errors = validateAll(skillId, intentName, example, null);
  if (errors) {
    return errors;
  }
  const url = examplesEndpoint(
    skillId,
    useNewIAUpdatedAccess,
    assistantId,
    intentName
  );
  const params = {
    text: example.text,
    mentions: example.mentions
  };
  return axios
    .post(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

/** Update an example
 * @param {string} skillId The target skill id
 * @param {string} intentName The intent name which the example belongs to.
 * @param {string} text The original example text
 * @param {object} example The new example
 * @returns {axios} An axios post call that returns the new example
 */
export const updateExample = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string,
  text: string,
  example: any
) => {
  const errors = validateAll(skillId, intentName, example, null);
  if (errors) {
    return errors;
  }

  var params = {
    text: example.text,
    mentions: example.mentions
  };
  return axios
    .post(
      exampleEndpoint(
        skillId,
        useNewIAUpdatedAccess,
        assistantId,
        intentName,
        text
      ),
      params
    )
    .then(response => response.data)
    .catch(error => {
      const err = _.get(error, 'response.data') || error;
      err.transactionId = error.transactionId;
      return Promise.reject(err);
    });
};

export const removeExamples = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string,
  examples: any
) => {
  const errors = validateAll(skillId, intentName, null, examples);
  if (errors) {
    return errors;
  }

  //The user has checked a number of examples to delete. Delete just those selected.
  const url = batchRemoveEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  const value = [];
  examples.forEach(example => {
    value.push({ id: example.text });
  });
  const params = {
    operation: 'delete',
    resource: 'example',
    references: {
      intent_id: intentName
    },
    value
  };
  return axios
    .patch(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};

/**
 * Fetch the set of examples for the given intent
 * @param {string} skillId The target skill id
 * @param {string} intentName The intent name which the example belongs to.
 * @returns {axios} An axios post call that returns the example array
 */
export const listExamples = (skillId, intentName) => {
  const errors = validateAll(skillId, intentName, null, null);
  if (errors) {
    return errors;
  }
  const url = examplesEndpoint(skillId, false, '', intentName);
  return axios
    .get(url)
    .then(response => response.data)
    .catch(error => Promise.reject(error));
};

export const moveExamples = (
  skillId: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  intentName: string,
  value: any
) => {
  const errors = validateAll(skillId, intentName, null, null);
  if (errors) {
    return errors;
  }
  const url = batchRemoveEndpoint(skillId, useNewIAUpdatedAccess, assistantId);
  const params = {
    operation: 'move',
    resource: 'example',
    references: {
      intent_id: intentName
    },
    value
  };
  return axios
    .patch(url, params)
    .then(response => response.data)
    .catch(error => Promise.reject(_.get(error, 'response.data') || error));
};
